import { useState } from 'react';

import { Skeleton, WidgetError } from '../pages/Dashboard/WidgetState';
import { Toaster, useToasts } from '../pages/Listings/useToasts';
import { Button, cn, Select } from './ui';


/** Inner: owns the local edit state. Re-keyed by a stable signature of the loaded
 * values (see the outer) so a refetch re-seeds edits by remount rather than a
 * setState-in-effect (react-hooks/set-state-in-effect) — mirrors CopyModalInner's
 * `key={part.id}` trick. Renders its own `<Toaster>`: `useToasts` is a local
 * queue, so the page-level Toaster (a separate instance) can't surface our push. */
function PartChecklistBody(props) {
  const {
    title, savedMessage, canWrite, saving, onSave, onSaved, summary, items,
  } = props;
  const { toasts, push } = useToasts();

  const [values, setValues] = useState(() => {
    const seed = {};
    items.forEach((item) => {
      seed[item.code] = item.value ?? '';
    });
    return seed;
  });

  const setValue = (code, value) => setValues((previous) => ({ ...previous, [code]: value }));

  const handleSave = () => {
    // Send every rendered code — including blanks — so clearing a field removes it
    // server-side (the PATCH merge drops blank values).
    const payload = {};
    items.forEach((item) => {
      payload[item.code] = values[item.code] ?? '';
    });
    onSave(payload, {
      onSuccess: () => {
        push(savedMessage);
        onSaved?.();
      },
    });
  };

  const requiredMissing = summary?.required_missing ?? 0;
  // `failed` is only present on checklists with a pass/fail domain (#277 inspection);
  // a free-text checklist (#273 item specifics) simply omits it and renders no badge.
  const failed = summary?.failed ?? 0;

  return (
    <div className="flex flex-col gap-1.5">
      <span className="flex items-center justify-between gap-2">
        <span className="text-[10.5px] font-semibold uppercase tracking-[0.5px] text-ink-subtle">
          {title}
        </span>

        <span className="flex items-center gap-2">
          <span className="text-xs text-ink-subtle">
            {`${summary?.filled ?? 0} / ${summary?.total ?? items.length}`}
          </span>

          {failed > 0
            ? (
              <span className="rounded-full bg-danger-soft px-2 py-[2px] text-[9.5px] font-semibold text-danger">
                {`${failed} failed`}
              </span>
            )
            : null}

          {requiredMissing > 0
            ? (
              <span className="rounded-full bg-warning-soft px-2 py-[2px] text-[9.5px] font-semibold text-warning">
                {`${requiredMissing} required missing`}
              </span>
            )
            : null}
        </span>
      </span>

      <div className="flex flex-col gap-[7px]">
        {items.map((item) => {
          const current = values[item.code] ?? '';
          const missingRequired = item.is_required && !current;
          const dotClass = current
            ? 'bg-success'
            : missingRequired
              ? 'bg-danger'
              : 'bg-ink-subtle/40';

          return (
            <span
              key={item.code}
              className="flex items-center gap-2 rounded-lg border border-hairline bg-surface-2 px-3 py-2"
            >
              <span className="flex w-[110px] shrink-0 items-center gap-1.5">
                <span title={item.name} className="truncate text-table text-ink-subtle">
                  {item.name}
                </span>

                {item.is_required
                  ? (
                    <span
                      className={cn(
                        'rounded-full px-1.5 py-px text-[8.5px] font-semibold uppercase tracking-[0.4px]',
                        missingRequired ? 'bg-danger-soft text-danger' : 'bg-warning-soft text-warning',
                      )}
                    >
                      Required
                    </span>
                  )
                  : null}
              </span>

              {item.options?.length > 0
                ? (
                  <Select
                    value={current}
                    disabled={!canWrite}
                    className="min-w-0 flex-1"
                    selectClassName="h-8 rounded-md bg-transparent border-hairline text-table"
                    onChange={(event) => setValue(item.code, event.target.value)}
                  >
                    <option value="">—</option>

                    {item.options.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                )
                : (
                  <input
                    value={current}
                    disabled={!canWrite}
                    className="min-w-0 flex-1 bg-transparent text-table text-ink outline-none disabled:opacity-70"
                    onChange={(event) => setValue(item.code, event.target.value)}
                  />
                )}

              <span
                aria-hidden
                className={cn(
                  'size-1.5 shrink-0 rounded-full',
                  dotClass,
                  missingRequired && 'text-danger',
                )}
              />
            </span>
          );
        })}
      </div>

      {canWrite
        ? (
          <span className="mt-1 flex justify-end">
            <Button
              disabled={saving}
              className={cn(
                'bg-brand-gradient h-auto rounded-lg px-[18px] py-2',
                'text-table font-semibold text-white',
              )}
              onClick={handleSave}
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </span>
        )
        : null}

      <Toaster toasts={toasts} />
    </div>
  );
}

PartChecklistBody.displayName = 'PartChecklistBody';


/**
 * Shared per-part checklist panel.
 *
 * WHY THIS IS SHARED: #273 (eBay item specifics) and #277 (inspection checklist)
 * render the identical widget over the identical `{summary, items[]}` payload the
 * backend's shared `checklists.py` builder emits — required badge, filled counter,
 * options-as-select vs free-text input, per-row state dot, save-all-including-blanks.
 * Duplicating ~150 lines of that JSX would give two checklists that drift visually
 * and behaviourally for no reason.
 *
 * WHAT STAYS WITH EACH FEATURE: the data wiring — which endpoint, which PATCH body
 * key, and which queries to invalidate on save. Those genuinely differ, so each
 * feature keeps its own thin wrapper + mutation hook and passes `query`/`onSave` in.
 *
 * Props: `query` (a TanStack query for the checklist endpoint), `title`,
 * `savedMessage`, `canWrite`, `saving`, `onSave(valuesByCode, { onSuccess })`,
 * `onSaved`. Renders nothing when the checklist has no items.
 */
function PartChecklist(props) {
  const {
    query, title, savedMessage, canWrite, saving, onSave, onSaved,
  } = props;

  if (query.isPending) {
    return (
      <div className="flex flex-col gap-1.5">
        <Skeleton className="h-3.5 w-44" />
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-9 w-full" />
      </div>
    );
  }

  if (query.isError) {
    return <WidgetError className="py-6" onRetry={() => query.refetch()} />;
  }

  const items = query.data?.items || [];
  if (!items.length) {
    return null;
  }

  // Remount the body whenever the fetched values change so local edit state
  // re-seeds from props without a setState-in-effect.
  const signature = items.map((item) => `${item.code}=${item.value ?? ''}`).join('|');

  return (
    <PartChecklistBody
      items={items}
      title={title}
      key={signature}
      saving={saving}
      canWrite={canWrite}
      savedMessage={savedMessage}
      summary={query.data?.summary}
      onSave={onSave}
      onSaved={onSaved}
    />
  );
}

PartChecklist.displayName = 'PartChecklist';
export default PartChecklist;
